import {
  mdiAccountGroupOutline,
  mdiArrowUpBoldCircleOutline,
  mdiBellRingOutline,
  mdiCashMultiple,
  mdiDomain,
  mdiFileDocumentOutline,
  mdiGauge,
  mdiKeyOutline,
  mdiMessageAlertOutline,
  mdiShieldAccountOutline,
  mdiPackageVariantClosed,
  mdiShieldCheckOutline,
  mdiTune,
} from '@mdi/js';
import i18n from '@/i18n';

const subscriptionBreadcrumbs = (label, to) => [
  { label: i18n.global.t('cauldron.platform.label'), to: '/profile' },
  { label: i18n.global.t('menu_my_subscription'), to: '/platform/subscription/upgrade' },
  { label, to },
];

const subscriptionRouteMeta = (title, icon, to) => ({
  cauldron: 'platform',
  title,
  icon,
  requiresAuth: true,
  hideNewsletter: true,
  hideFooter: true,
  favoritable: false,
  breadcrumbs: subscriptionBreadcrumbs(title, to),
});

export default [
  {
    path: '/account/purchases/:id',
    name: 'purchase-checkout',
    component: () => import('@/pages/account/PurchaseCheckout.vue'),
    meta: { requiresAuth: true, hideNewsletter: true, hideFooter: true, favoritable: false },
  },
  {
    meta: {
      cauldron: 'platform',
      title: i18n.global.t('api_keys.page_title'),
      icon: mdiKeyOutline,
      requiresAuth: true,
      hideNewsletter: true,
      hideFooter: true,
      favoritable: false,
    },
    path: '/account/api-keys',
    name: 'api-keys',
    component: () => import('@/pages/account/ApiKeys.vue'),
  },
  {
    path: '/account/exports',
    redirect: { name: 'export-jobs' },
  },
  {
    meta: {
      cauldron: 'platform',
      title: i18n.global.t('feedback.mine.page_title'),
      icon: mdiMessageAlertOutline,
      requiresAuth: true,
      hideNewsletter: true,
      hideFooter: true,
      favoritable: false,
    },
    path: '/account/feedback',
    name: 'my-feedback',
    component: () => import('@/features/feedback/pages/MyFeedback.vue'),
  },
  {
    meta: {
      cauldron: 'platform',
      title: i18n.global.t('profile.tabs.security'),
      icon: mdiShieldAccountOutline,
      requiresAuth: true,
      hideNewsletter: true,
      hideFooter: true,
      favoritable: false,
      breadcrumbs: [
        { label: i18n.global.t('cauldron.platform.label'), to: '/profile' },
        { label: i18n.global.t('profile.tabs.security'), to: '/account/security' },
      ],
    },
    path: '/account/security',
    name: 'account.security',
    component: () => import('@/pages/account/Security.vue'),
  },
  {
    meta: {
      cauldron: 'platform',
      title: i18n.global.t('profile.tabs.onboarding'),
      icon: mdiTune,
      requiresAuth: true,
      hideNewsletter: true,
      hideFooter: true,
      favoritable: false,
      breadcrumbs: [
        { label: i18n.global.t('cauldron.platform.label'), to: '/profile' },
        { label: i18n.global.t('profile.tabs.onboarding'), to: '/account/preferences' },
      ],
    },
    path: '/account/preferences',
    name: 'account.preferences',
    component: () => import('@/pages/account/Preferences.vue'),
  },
  {
    meta: {
      cauldron: 'platform',
      title: i18n.global.t('notification_preferences.title'),
      icon: mdiBellRingOutline,
      requiresAuth: true,
      hideNewsletter: true,
      hideFooter: true,
      favoritable: false,
    },
    path: '/notifications/preferences',
    name: 'notification-preferences',
    component: () =>
      import(
        '@/features/notifications/pages/NotificationPreferencesPage.vue'
      ),
  },
  {
    meta: {
      title: i18n.global.t('page_titles.verify_email_change'),
      formScreen: true,
    },
    path: '/verify-email/:token',
    name: 'verify-email-change',
    component: () => import('@/pages/account/VerifyEmailChange'),
  },
  {
    meta: {
      title: i18n.global.t('page_titles.login'),
      formScreen: true,
    },
    path: '/login',
    name: 'login',
    component: () => import('@/pages/account/Login'),
  },
  {
    meta: {
      title: i18n.global.t('page_titles.register'),
      formScreen: true,
    },
    path: '/register',
    name: 'register',
    component: () => import('@/pages/account/Register'),
  },
  {
    meta: {
      title: i18n.global.t('page_titles.activate_email'),
      formScreen: true,
      requiresAuth: false,
    },
    path: '/account/activate',
    name: 'activate',
    component: () => import('@/pages/account/CheckActivationCode'),
  },
  {
    meta: {
      title: i18n.global.t('page_titles.resend_activation'),
      formScreen: true,
    },
    path: '/account/activation/resend',
    name: 'resend-activation',
    component: () => import('@/pages/account/ResendActivationCode'),
  },
  {
    meta: {
      title: i18n.global.t('page_titles.return_subscription'),
    },
    path: '/subscription/:subscriptionState/:subscriptionId',
    name: 'subscription',
    component: () => import('@/pages/account/Subscription.vue'),
  },
  {
    meta: {
      title: i18n.global.t('page_titles.accept_tenant_invitation'),
      formScreen: true,
    },
    path: '/tenant-invitations/:recoverCode',
    name: 'tenant-invitations.accept',
    component: () => import('@/pages/account/AcceptTenantInvitation'),
  },
  {
    meta: {
      title: i18n.global.t('page_titles.recover_password'),
      formScreen: true,
    },
    path: '/account/recover',
    name: 'recover',
    component: () => import('@/pages/account/SendPasswordRecovery'),
  },
  {
    meta: {
      title: i18n.global.t('page_titles.recover_password'),
      formScreen: true,
    },
    path: '/account/recover/:recoverCode',
    name: 'recover-password',
    component: () => import('@/pages/account/RecoverPassword'),
  },
  {
    meta: {
      cauldron: 'platform',
      title: i18n.global.t('menu_my_subscription'),
      icon: mdiPackageVariantClosed,
      requiresAuth: true,
      hideNewsletter: true,
      hideFooter: true,
      favoritable: false,
      breadcrumbs: [
        { label: i18n.global.t('cauldron.platform.label'), to: '/profile' },
        { label: i18n.global.t('menu_my_subscription'), to: '/platform/subscription/upgrade' },
      ],
    },
    path: '/my-subscription',
    name: 'my-subscription',
    redirect: { name: 'subscription.upgrade' },
  },
  {
    meta: subscriptionRouteMeta(
      i18n.global.t('subscription_nav.members'),
      mdiAccountGroupOutline,
      '/platform/subscription/members',
    ),
    path: '/platform/subscription/members',
    name: 'subscription.members',
    component: () => import('@/pages/account/tenant/TenantMembers.vue'),
  },
  {
    meta: subscriptionRouteMeta(
      i18n.global.t('subscription_nav.tokens'),
      mdiCashMultiple,
      '/platform/subscription/tokens',
    ),
    path: '/platform/subscription/tokens',
    name: 'subscription.tokens',
    component: () => import('@/pages/platform/subscription/SubscriptionTokens.vue'),
  },
  {
    meta: subscriptionRouteMeta(
      i18n.global.t('subscription_nav.quotas'),
      mdiGauge,
      '/platform/subscription/quotas',
    ),
    path: '/platform/subscription/quotas',
    name: 'subscription.quotas',
    component: () => import('@/pages/platform/subscription/SubscriptionQuotas.vue'),
  },
  {
    meta: subscriptionRouteMeta(
      i18n.global.t('subscription_nav.entitlements'),
      mdiShieldCheckOutline,
      '/platform/subscription/entitlements',
    ),
    path: '/platform/subscription/entitlements',
    name: 'subscription.entitlements',
    component: () => import('@/pages/platform/subscription/SubscriptionEntitlements.vue'),
  },
  {
    meta: subscriptionRouteMeta(
      i18n.global.t('subscription_nav.upgrade'),
      mdiArrowUpBoldCircleOutline,
      '/platform/subscription/upgrade',
    ),
    path: '/platform/subscription/upgrade',
    name: 'subscription.upgrade',
    component: () => import('@/pages/platform/subscription/SubscriptionUpgrade.vue'),
  },
  {
    meta: subscriptionRouteMeta(
      i18n.global.t('subscription_nav.invoices'),
      mdiFileDocumentOutline,
      '/platform/subscription/invoices',
    ),
    path: '/platform/subscription/invoices',
    name: 'subscription.invoices',
    component: () => import('@/pages/platform/subscription/SubscriptionInvoices.vue'),
  },
  {
    meta: {
      cauldron: 'platform',
      title: i18n.global.t('page_titles.menu_subscriptions'),
      icon: mdiPackageVariantClosed,
      formScreen: true,
      requiresAuth: false,
    },
    path: '/subscriptions',
    name: 'subscriptions',
    component: () => import('@/pages/products/Products.vue'),
  },
  {
    path: '/subscriptions-auth',
    redirect: { name: 'subscription.upgrade' },
  },
  {
    meta: {
      cauldron: 'platform',
      title: i18n.global.t('subscriptions.welcome.title'),
      formScreen: true,
      requiresAuth: false,
    },
    path: '/welcome/:token?',
    name: 'subscription-welcome',
    component: () => import('@/pages/subscriptions/Welcome.vue'),
  },
  {
    meta: {
      cauldron: 'platform',
      title: i18n.global.t('page_titles.menu_subscriptions'),
      icon: mdiPackageVariantClosed,
      formScreen: true,
      requiresAuth: false,
    },
    path: '/subscriptions/#upgrade',
    name: 'subscriptions-upgrade-flex',
    component: () => import('@/pages/products/Products.vue'),
  },
  {
    meta: {
      cauldron: 'platform',
      title: i18n.global.t('page_titles.menu_subscriptions'),
      icon: mdiPackageVariantClosed,
      formScreen: true,
      requiresAuth: false,
    },
    path: '/subscriptions/#upgrade',
    name: 'subscriptions-upgrade-tools',
    component: () => import('@/pages/products/Products.vue'),
  },
  {
    meta: {
      cauldron: 'platform',
      title: i18n.global.t('page_titles.menu_subscriptions'),
      icon: mdiPackageVariantClosed,
      formScreen: true,
      requiresAuth: false,
    },
    path: '/subscriptions/#upgrade',
    name: 'subscriptions-upgrade-basic',
    component: () => import('@/pages/products/Products.vue'),
  },
  {
    meta: {
      cauldron: 'platform',
      title: i18n.global.t('tenant.management.title'),
      icon: mdiDomain,
      requiresAuth: true,
      hideNewsletter: true,
      hideFooter: true,
      favoritable: false,
      breadcrumbs: [
        { label: i18n.global.t('cauldron.platform.label'), to: '/profile' },
        { label: i18n.global.t('tenant.management.title'), to: '/tenants' },
      ],
    },
    path: '/tenants',
    name: 'tenants.index',
    component: () => import('@/pages/account/tenant/TenantIndex.vue'),
  },
  {
    path: '/tenant/members',
    name: 'tenant.members',
    redirect: { name: 'subscription.members' },
  },
];
